<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <report-meals-select-company />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="users"
      class="striped-table ordera-box-shadow"
      dense
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-account-off </v-icon>
          <div class="text-h6 grey--Text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>

      <template v-slot:item.user_id="{ item }">
        {{ item.user_id }}
      </template>

      <template v-slot:item.user_name="{ item }">
        <span @click="openUserDialog(item)" class="cursor-pointer">
          {{ toLatin(item.user_name) }}
        </span>
      </template>

      <template v-slot:item.orders_count="{ item }">
        {{ item.orders_count }}
      </template>

      <template v-slot:item.orders_total="{ item }">
        {{
          formatMoneyFromTotals(
            item.totals && item.totals.orders_total_cents,
            item.totals && item.totals.orders_total
          )
        }}
      </template>

      <template v-slot:item.spent_on_credits="{ item }">
        {{
          formatMoneyFromTotals(
            item.totals && item.totals.spent_on_credits_cents,
            item.totals && item.totals.spent_on_credits
          )
        }}
      </template>

      <template v-slot:item.current_credit_balance="{ item }">
        {{
          formatMoneyFromTotals(
            item.totals && item.totals.current_credit_balance_cents,
            item.totals && item.totals.current_credit_balance
          )
        }}
      </template>

      <template v-slot:item.action="{ item }">
        <circle-button
          icon="mdi-format-list-bulleted"
          @click="openUserDialog(item)"
          color="primary"
          :tooltip="$t('tooltip_order_details')"
        ></circle-button>
      </template>
    </v-data-table>

    <credit-user-details-dialog
      v-model="showUserDialog"
      :user="selectedUser"
      :company-id="selectedCompanyId"
    />

    <credit-user-details-dialog-more-restaurants
      v-model="showUserDialogMR"
      :user="selectedUserMR"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsSelectCompany from "@/components/report/more-restaurants/fields/ReportMealsSelectCompanyField.vue";
import CreditUserDetailsDialog from "@/components/report/more-restaurants/dialogs/CreditUserDetailsDialog.vue";
import CreditUserDetailsDialogMoreRestaurants from "@/components/report/more-restaurants/dialogs/CreditUserDetailsDialogMoreRestaurants.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  components: {
    ReportMealsSelectCompany,
    CreditUserDetailsDialog,
    CreditUserDetailsDialogMoreRestaurants,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    report: null,
    loading: false,
    selectedUser: null,
    showUserDialog: false,
    selectedUserMR: null,
    showUserDialogMR: false,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    selectedCompany() {
      return this.$store.state.report.selectedMealCompany;
    },
    selectedCompanyId() {
      const sc = this.selectedCompany;
      if (Array.isArray(sc)) {
        const first = sc[0];
        return first && (typeof first === "object" ? first.id : first);
      } else if (sc && typeof sc === "object") {
        return sc.id;
      }
      return sc;
    },
    users() {
      if (!this.report) return [];

      // Support multiple response shapes:
      // - { users: [...] }
      // - single user object with user_id, orders, totals
      // - array of users
      let userArray = [];
      if (Array.isArray(this.report.users)) {
        userArray = this.report.users;
      } else if (this.report.user_id) {
        userArray = [this.report];
      } else if (Array.isArray(this.report)) {
        userArray = this.report;
      }

      return userArray.map((u, index) => {
        const orders = Array.isArray(u.orders) ? u.orders.filter((o) => o && o.id) : [];
        const totals = u.totals || {};
        return {
          ...u,
          num: index + 1,
          orders_count: orders.length,
          orders_valid: orders,
          totals,
        };
      });
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          width: "100px",
          print: false,
        },
        { text: this.$t("user_id"), value: "user_id", sortable: false },
        { text: this.$t("name"), value: "user_name", sortable: false },
        { text: this.$t("orders"), value: "orders_count", align: "center" },
        { text: this.$t("orders_total"), value: "orders_total", align: "right" },
        { text: this.$t("spent_on_credits"), value: "spent_on_credits", align: "right" },
        {
          text: this.$t("credit_balance"),
          value: "current_credit_balance",
          align: "right",
        },
        {
          text: this.$t("report"),
          value: "action",
          sortable: false,
          align: "center",
          print: false,
        },
      ];
    },
    subtitle() {
      const from = this.filters?.dates?.from || "";
      const to = this.filters?.dates?.to || "";
      return `${from} - ${to}`;
    },
  },
  methods: {
    async getUsers() {
      if (!this.selectedCompany) {
        this.report = null;
        return;
      }

      this.loading = true;
      this.report = null;
      try {
        // Base params (API expects `date` and `date2`)
        const params = {
          date_from: this.filters.dates.from,
          date_to: this.filters.dates.to,
        };
        // Debug what we'll send
        console.debug("getCreditUser params:", params);

        // Build single company_id param (use first company if array)
        let companyId = null;
        const sc = this.selectedCompany;
        if (Array.isArray(sc)) {
          const first = sc[0];
          companyId = first && (typeof first === "object" ? first.id : first);
        } else if (sc && typeof sc === "object") {
          companyId = sc.id;
        } else if (sc) {
          companyId = sc;
        }

        if (companyId) {
          params.company_id = companyId;
        } else {
          // nothing to query without a company id
          this.report = null;
          this.loading = false;
          return;
        }

        const { data } = await reportService.getCreditUser(params);

        this.report = data;
      } catch (error) {
        this.report = null;
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    openUserDialog(item) {
      // ensure orders_valid exists even if item is raw API object
      const orders_valid = Array.isArray(item.orders)
        ? item.orders.filter((o) => o && o.id)
        : item.orders_valid || [];
      this.selectedUser = { ...item, orders_valid };
      this.showUserDialog = true;
    },
    openUserDialogMR(item) {
      this.selectedUserMR = item;
      this.showUserDialogMR = true;
    },
    formatMoneyCents(cents) {
      if (cents === null || cents === undefined || cents === "") return "";
      const n = Number(cents);
      if (Number.isNaN(n)) return "";
      return (n / 100).toFixed(2);
    },
    formatMoneyFromTotals(centsVal, fallback) {
      if (centsVal !== null && centsVal !== undefined && centsVal !== "") {
        return this.formatMoneyCents(centsVal);
      }
      if (fallback !== null && fallback !== undefined && fallback !== "") {
        const n = Number(fallback);
        if (Number.isNaN(n)) return "";
        return n.toFixed(2);
      }
      return "";
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatUsersForExport() {
      const exportHeaders = this.getExportHeaders();
      return this.users.map((item) => {
        const exportItem = {};
        exportHeaders.forEach((header) => {
          if (header.value === "user_name") {
            exportItem[header.value] = this.toLatin(item[header.value]);
          } else if (header.value === "orders_total") {
            exportItem[header.value] = this.formatMoneyFromTotals(
              item.totals && item.totals.orders_total_cents,
              item.totals && item.totals.orders_total
            );
          } else if (header.value === "spent_on_credits") {
            exportItem[header.value] = this.formatMoneyFromTotals(
              item.totals && item.totals.spent_on_credits_cents,
              item.totals && item.totals.spent_on_credits
            );
          } else if (header.value === "current_credit_balance") {
            exportItem[header.value] = this.formatMoneyFromTotals(
              item.totals && item.totals.current_credit_balance_cents,
              item.totals && item.totals.current_credit_balance
            );
          } else {
            exportItem[header.value] =
              item[header.value] ?? (typeof item[header.value] === "number" ? 0 : "");
          }
        });
        return exportItem;
      });
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.getUsers();
      },
      deep: true,
    },
    selectedCompany: {
      handler() {
        this.getUsers();
      },
      immediate: true,
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("credit_users")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("users"),
            headers: this.getExportHeaders(),
            items: this.formatUsersForExport(),
            title: [this.$t("report"), this.$t("credit_users")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.formatUsersForExport(),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("credit_users")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
};
</script>
